/**
 * MultiplayerMenu -- UI for P2P connection.
 * Shown from PauseMenu -> "Multijugador" button.
 *
 * Layout:
 * - Title: "Multijugador"
 * - Two buttons: "Crear Sala" / "Unirse a Sala"
 * - Create flow: shows offer code textarea (copy), then answer input
 * - Join flow: shows offer input, then answer textarea (copy)
 * - Status indicator: Conectando... / Conectado / Desconectado
 */

export class MultiplayerMenu {
  /**
   * @param {import('../network/P2PManager.js').P2PManager} p2pManager
   */
  constructor(p2pManager) {
    this.p2p = p2pManager;
    this.isOpen = false;
    this._phase = 'menu'; // 'menu' | 'host_offer' | 'host_answer' | 'join_offer' | 'join_answer' | 'connected'
    this._buildUI();
  }

  // ================================================================
  // DOM construction
  // ================================================================

  _buildUI() {
    // --- Overlay ---
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.75);
      z-index: 920;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
    `;

    // --- Panel ---
    this._panel = document.createElement('div');
    this._panel.style.cssText = `
      background: #2A2A2A;
      border: 4px solid;
      border-top-color: #5A5A5A;
      border-left-color: #5A5A5A;
      border-bottom-color: #1A1A1A;
      border-right-color: #1A1A1A;
      padding: 24px 32px;
      min-width: 420px;
      max-width: 520px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      image-rendering: pixelated;
    `;

    // --- Title ---
    const title = document.createElement('div');
    title.textContent = 'MULTIJUGADOR';
    title.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 24px; font-weight: bold;
      color: #FFFFFF;
      text-shadow: 3px 3px 0 #3F3F3F;
      margin-bottom: 8px;
      letter-spacing: 3px;
    `;
    this._panel.appendChild(title);

    // --- Status text ---
    this._statusText = document.createElement('div');
    this._statusText.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 13px;
      color: #AAAAAA;
      text-align: center;
      min-height: 18px;
      margin-bottom: 4px;
    `;
    this._statusText.textContent = 'Elige una opcion para conectar con otro jugador.';
    this._panel.appendChild(this._statusText);

    // --- Menu buttons container ---
    this._menuBtns = document.createElement('div');
    this._menuBtns.style.cssText = 'display:flex; flex-direction:column; gap:10px; align-items:center;';

    this._btnCreate = this._makeButton('Crear Sala', () => this._onCreate());
    this._btnJoin = this._makeButton('Unirse a Sala', () => this._onJoinStart());
    this._menuBtns.appendChild(this._btnCreate);
    this._menuBtns.appendChild(this._btnJoin);
    this._panel.appendChild(this._menuBtns);

    // --- Textarea: shows generated code (offer or answer) ---
    this._codeOutput = document.createElement('textarea');
    this._codeOutput.readOnly = true;
    this._codeOutput.style.cssText = `
      display: none;
      width: 100%; height: 80px;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      background: #1A1A1A; color: #55FF55;
      border: 2px solid #3A3A3A;
      padding: 6px;
      resize: none;
      box-sizing: border-box;
    `;
    this._panel.appendChild(this._codeOutput);

    // --- Copy button for output code ---
    this._btnCopy = this._makeButton('Copiar Codigo', () => this._copyCode());
    this._btnCopy.style.display = 'none';
    this._panel.appendChild(this._btnCopy);

    // --- Input: paste code from other player ---
    this._codeInputLabel = document.createElement('div');
    this._codeInputLabel.style.cssText = `
      display: none;
      font-family: 'Courier New', monospace;
      font-size: 12px; color: #CCCCCC;
      align-self: flex-start;
      margin-top: 4px;
    `;
    this._panel.appendChild(this._codeInputLabel);

    this._codeInput = document.createElement('textarea');
    this._codeInput.style.cssText = `
      display: none;
      width: 100%; height: 60px;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      background: #1A1A1A; color: #FFFF55;
      border: 2px solid #3A3A3A;
      padding: 6px;
      resize: none;
      box-sizing: border-box;
    `;
    this._codeInput.placeholder = 'Pega el codigo aqui...';
    this._panel.appendChild(this._codeInput);

    // --- Submit pasted code button ---
    this._btnSubmit = this._makeButton('Aceptar', () => this._onSubmitCode());
    this._btnSubmit.style.display = 'none';
    this._panel.appendChild(this._btnSubmit);

    // --- Disconnect button (shown when connected) ---
    this._btnDisconnect = this._makeButton('Desconectar', () => this._onDisconnect());
    this._btnDisconnect.style.display = 'none';
    this._panel.appendChild(this._btnDisconnect);

    // --- Close button ---
    const btnClose = this._makeButton('Cerrar', () => this.hide());
    this._panel.appendChild(btnClose);

    this._overlay.appendChild(this._panel);
    document.body.appendChild(this._overlay);
  }

  /**
   * Create a Minecraft-style button.
   */
  _makeButton(text, onClick) {
    const btn = document.createElement('div');
    btn.textContent = text;
    btn.style.cssText = `
      width: 240px;
      padding: 7px 0;
      text-align: center;
      font-family: 'Courier New', monospace;
      font-size: 15px; font-weight: bold;
      color: #E0E0E0;
      text-shadow: 2px 2px 0 #3F3F3F;
      background: #737373;
      border: 3px solid;
      border-top-color: #A0A0A0;
      border-left-color: #A0A0A0;
      border-bottom-color: #505050;
      border-right-color: #505050;
      cursor: pointer;
      user-select: none;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#878787';
      btn.style.color = '#FFFFA0';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#737373';
      btn.style.color = '#E0E0E0';
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ================================================================
  // Flow: Host creates a room
  // ================================================================

  async _onCreate() {
    this._phase = 'host_offer';
    this._resetFields();
    this._menuBtns.style.display = 'none';
    this._statusText.textContent = 'Generando codigo de sala...';
    this._statusText.style.color = '#FFAA00';

    try {
      const offer = await this.p2p.createOffer();

      this._codeOutput.value = offer;
      this._codeOutput.style.display = 'block';
      this._btnCopy.style.display = 'block';
      this._statusText.textContent = 'Copia este codigo y envíaselo a tu amigo. Luego pega su respuesta abajo.';
      this._statusText.style.color = '#55FF55';

      // Show answer input
      this._phase = 'host_answer';
      this._codeInputLabel.textContent = 'Pegar respuesta del amigo:';
      this._codeInputLabel.style.display = 'block';
      this._codeInput.style.display = 'block';
      this._codeInput.value = '';
      this._btnSubmit.style.display = 'block';
    } catch (err) {
      this._statusText.textContent = 'Error: ' + err.message;
      this._statusText.style.color = '#FF5555';
      this._menuBtns.style.display = 'flex';
    }
  }

  // ================================================================
  // Flow: Guest joins a room
  // ================================================================

  _onJoinStart() {
    this._phase = 'join_offer';
    this._resetFields();
    this._menuBtns.style.display = 'none';
    this._statusText.textContent = 'Pega el codigo que te envio el host.';
    this._statusText.style.color = '#55AAFF';

    this._codeInputLabel.textContent = 'Pegar codigo del host:';
    this._codeInputLabel.style.display = 'block';
    this._codeInput.style.display = 'block';
    this._codeInput.value = '';
    this._btnSubmit.style.display = 'block';
  }

  // ================================================================
  // Submit pasted code (both host-answer and join-offer)
  // ================================================================

  async _onSubmitCode() {
    const code = this._codeInput.value.trim();
    if (!code) {
      this._statusText.textContent = 'Pega un codigo primero.';
      this._statusText.style.color = '#FF5555';
      return;
    }

    try {
      if (this._phase === 'host_answer') {
        // Host pastes guest's answer
        this._statusText.textContent = 'Conectando...';
        this._statusText.style.color = '#FFAA00';
        await this.p2p.acceptAnswer(code);
        this._statusText.textContent = 'Esperando conexion...';
        // Connection event handled via onConnect callback
      } else if (this._phase === 'join_offer') {
        // Guest pastes host's offer, generates answer
        this._statusText.textContent = 'Generando respuesta...';
        this._statusText.style.color = '#FFAA00';

        const answer = await this.p2p.acceptOffer(code);

        this._phase = 'join_answer';
        this._codeInput.style.display = 'none';
        this._codeInputLabel.style.display = 'none';
        this._btnSubmit.style.display = 'none';

        this._codeOutput.value = answer;
        this._codeOutput.style.display = 'block';
        this._btnCopy.style.display = 'block';
        this._statusText.textContent = 'Copia esta respuesta y envíasela al host. Esperando conexion...';
        this._statusText.style.color = '#55FF55';
      }
    } catch (err) {
      this._statusText.textContent = 'Error: codigo invalido. ' + err.message;
      this._statusText.style.color = '#FF5555';
    }
  }

  // ================================================================
  // Copy code to clipboard
  // ================================================================

  async _copyCode() {
    try {
      await navigator.clipboard.writeText(this._codeOutput.value);
      this._btnCopy.textContent = 'Copiado!';
      setTimeout(() => { this._btnCopy.textContent = 'Copiar Codigo'; }, 1500);
    } catch (_) {
      // Fallback: select the text for manual copy
      this._codeOutput.select();
      document.execCommand('copy');
      this._btnCopy.textContent = 'Copiado!';
      setTimeout(() => { this._btnCopy.textContent = 'Copiar Codigo'; }, 1500);
    }
  }

  // ================================================================
  // Disconnect
  // ================================================================

  _onDisconnect() {
    this.p2p.disconnect();
    this._showConnectedState(false);
  }

  // ================================================================
  // Helpers
  // ================================================================

  _resetFields() {
    this._codeOutput.style.display = 'none';
    this._codeOutput.value = '';
    this._btnCopy.style.display = 'none';
    this._codeInputLabel.style.display = 'none';
    this._codeInput.style.display = 'none';
    this._codeInput.value = '';
    this._btnSubmit.style.display = 'none';
    this._btnDisconnect.style.display = 'none';
  }

  /**
   * Called externally when connection state changes.
   * @param {boolean} connected
   */
  _showConnectedState(connected) {
    this._resetFields();
    if (connected) {
      this._phase = 'connected';
      this._menuBtns.style.display = 'none';
      this._statusText.textContent = 'Conectado con otro jugador!';
      this._statusText.style.color = '#55FF55';
      this._btnDisconnect.style.display = 'block';
    } else {
      this._phase = 'menu';
      this._menuBtns.style.display = 'flex';
      this._statusText.textContent = 'Desconectado.';
      this._statusText.style.color = '#AAAAAA';
    }
  }

  // ================================================================
  // Public API
  // ================================================================

  show() {
    this._overlay.style.display = 'flex';
    this.isOpen = true;
    // If already connected, show connected state
    if (this.p2p.isConnected) {
      this._showConnectedState(true);
    } else if (this._phase === 'menu') {
      this._resetFields();
      this._menuBtns.style.display = 'flex';
      this._statusText.textContent = 'Elige una opcion para conectar con otro jugador.';
      this._statusText.style.color = '#AAAAAA';
    }
  }

  hide() {
    this._overlay.style.display = 'none';
    this.isOpen = false;
  }
}
